import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import ExpenseFormInner from './ExpenseFormInner';

import { createExpense, createExpenseWithFile } from '../../api/expenses';
import { extractErrorMessage } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';

const EMPTY = {
  title: '',
  description: '',
  amount: '',
  category: 'OTHER',
  expenseType: '',
  paymentType: '',
  department: '',
  vendorId: null,
  productId: '',
  isRecurring: false,
  recurringType: '',
  transactionDate: '',
  bankDescription: '',
  comments: '',
};

/**
 * Modal-based create form for an Expense.
 *
 * If the user attaches a file, we send a multipart request; otherwise we
 * send the JSON payload directly. This mirrors the two supported variants
 * on the backend.
 */
export default function ExpenseForm({ open, onClose, onCreated }) {
  const qc = useQueryClient();
  const { user, depts } = useAuth();

  const defaultDept = (depts && depts[0]) || '';
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});

  // Reset whenever the modal opens — keep the user's primary dept as default.
  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, department: defaultDept });
      setFile(null);
      setErrors({});
    }
  }, [open, defaultDept]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        amount: Number(form.amount),
        category: form.category,
        expenseType: form.expenseType,
        paymentType: form.paymentType,
        department: form.department,
        vendorId: form.vendorId || null,
        productId: form.productId ? Number(form.productId) : null,
        isRecurring: !!form.isRecurring,
        recurringType: form.isRecurring ? form.recurringType || null : null,
        transactionDate: form.transactionDate || null,
        bankDescription: form.bankDescription.trim() || null,
        comments: form.comments.trim() || null,
      };
      return file
        ? createExpenseWithFile(payload, file)
        : createExpense(payload);
    },
    onSuccess: (created) => {
      toast.success('Expense created');
      qc.invalidateQueries({ queryKey: ['expenses'] });
      onCreated?.(created);
      onClose?.();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  function validate() {
    const next = {};
    if (!form.title.trim()) next.title = 'Title is required';
    if (!form.amount || Number(form.amount) <= 0)
      next.amount = 'Amount must be greater than zero';
    if (!form.category) next.category = 'Category is required';
    if (!form.expenseType) next.expenseType = 'Expense Type is required';
    if (!form.paymentType) next.paymentType = 'Payment Type is required';
    if (!form.department) next.department = 'Department is required';
    if (!form.productId) next.productId = 'Product is required';
    if (form.isRecurring && !form.recurringType)
      next.recurringType = 'Pick a recurring cadence';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function onSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New expense"
      description={`Filed by ${user?.username || 'you'}`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            onClick={onSubmit}
          >
            Create expense
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4">
        <ExpenseFormInner
          form={form}
          update={update}
          errors={errors}
          file={file}
          setFile={setFile}
        />
      </form>
    </Modal>
  );
}
